// M3-3: the achievements page was retired into the AchievementsSheet overlay, so
// there is no longer a `View<"achievements">` to derive from. The row shape is
// the account-level achievement record returned by the `getAchievementsSheet`
// worker RPC (achievement.getAll()): every achievement plus how many times it
// was earned at each difficulty (0/0/0 = still locked).
export type AchievementRow = {
	category: string;
	desc: string;
	name: string;
	slug: string;
	normal: number;
	hard: number;
	insane: number;
};

export type UnlockedAchievement = AchievementRow & { count: number };

export type AchievementsSummary = {
	// Completion is measured against non-Meta achievements only. The single
	// "Meta" achievement (Participation) is awarded just for opening the game,
	// so the original desktop page excludes it from the X / Y count.
	countable: number;
	unlockedCountable: number;
	unlocked: UnlockedAchievement[];
	locked: AchievementRow[];
};

export const ACHIEVEMENT_META_CATEGORY = "Meta";

// Times earned across the three countable difficulties (normal/hard/insane).
export const getAchievementEarnedCount = (achievement: AchievementRow) =>
	achievement.normal + achievement.hard + achievement.insane;

export const isAchievementUnlocked = (achievement: AchievementRow) =>
	getAchievementEarnedCount(achievement) > 0;

// Pure shaping used by the Achievements view: split earned vs locked and count
// completion over the non-Meta set. Order is preserved from the input so the
// engine's category grouping stays intact.
export const getAchievementsSummary = (
	achievements: readonly AchievementRow[],
): AchievementsSummary => {
	const unlocked: UnlockedAchievement[] = [];
	const locked: AchievementRow[] = [];
	let countable = 0;
	let unlockedCountable = 0;

	for (const achievement of achievements) {
		const isMeta = achievement.category === ACHIEVEMENT_META_CATEGORY;
		const earned = isAchievementUnlocked(achievement);

		if (!isMeta) {
			countable += 1;
			if (earned) {
				unlockedCountable += 1;
			}
		}

		if (earned) {
			unlocked.push({
				...achievement,
				count: getAchievementEarnedCount(achievement),
			});
		} else {
			locked.push(achievement);
		}
	}

	return { countable, unlockedCountable, unlocked, locked };
};
